import { SyncOutlined } from '@ant-design/icons';
import { type ECarrierVendorType } from '@mahawi/eshop-common/src/types';
import {
  Alert,
  Button,
  Descriptions,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import CopyToClipboard from '../components/copy-to-clipboard';
import DetailPageHeader from '../components/detail-page-header';
import ExternalLink from '../components/external-link';
import Link from '../components/link';
import StatusTag from '../components/status-tag';
import TableComponent from '../components/table';
import { EDateFormat } from '../fragments/date-format';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { getPacketTrackingUrl } from '../fragments/packet-tracking-url';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { carrierSyncPacketStatus } from '../reducers/carrier/reducer';
import { packetLoad } from '../reducers/packets/reducer';
import { type IAdminCarrierPacketStatus } from '../reducers/packets/types';
import { SPACING } from '../theme/tokens';

const { Text, Title } = Typography;

const statusColumns: ColumnsType<IAdminCarrierPacketStatus> = [
  {
    title: 'Status',
    dataIndex: 'status',
    render: (status: string): React.ReactElement => (
      <StatusTag status={status} />
    ),
    width: '25%',
  },
  {
    title: 'Carrier Status',
    dataIndex: 'carrierStatus',
    width: '35%',
  },
  {
    title: 'Note',
    dataIndex: 'note',
    render: (note: string | null): React.ReactElement => (
      <Text>{note ?? EMPTY_VALUE}</Text>
    ),
    width: '40%',
  },
];

function PacketContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const packetsInProcess = useSelector((s: RootState) => s.Packets.inProcess);
  const packetsPacket = useSelector((s: RootState) => s.Packets.packet);

  const { packetId } = useParams<{ packetId: string }>();

  useEffect((): void => {
    if (packetId) {
      dispatch(packetLoad(Number(packetId)));
    }
  }, [dispatch, packetId]);

  if (packetsInProcess || packetsPacket === undefined) {
    return <Skeleton active />;
  }

  if (packetsPacket === null) {
    return <Alert type="error" title="Shipment not found" />;
  }

  const packet = packetsPacket;
  const statuses = [...(packet.CarrierPacketStatuses ?? [])].sort(
    (a, b) => a.id - b.id,
  );
  const trackingUrl = getPacketTrackingUrl(
    packet.barcode,
    packet.CarrierService.CarrierVendorType.code,
  );

  return (
    <>
      <DetailPageHeader
        title={`Shipment ${packet.thirdPartyId}`}
        onReload={(): void => {
          dispatch(packetLoad(Number(packetId)));
        }}
        isReloadDisabled={packetsInProcess}
        actions={
          <Button
            icon={<SyncOutlined />}
            onClick={(): void => {
              dispatch(
                carrierSyncPacketStatus(
                  packet.thirdPartyId,
                  packet.Order.uuid,
                  packet.CarrierService.CarrierVendorType
                    .code as ECarrierVendorType,
                ),
              );
            }}
          >
            Sync status
          </Button>
        }
      />

      <Space size={SPACING.xl} orientation="vertical" style={{ width: '100%' }}>
        <Descriptions
          title="Shipment"
          bordered
          column={2}
          items={[
            {
              key: 'tracking-no',
              label: 'Tracking No.',
              children: (
                <Space>
                  {packet.thirdPartyId}
                  <CopyToClipboard
                    text={packet.thirdPartyId}
                    size="small"
                    type="text"
                  />
                </Space>
              ),
            },
            {
              key: 'barcode',
              label: 'Barcode',
              children: packet.barcode ? (
                <Space>
                  {trackingUrl ? (
                    <ExternalLink
                      to={trackingUrl}
                      label={packet.barcode}
                      ariaLabel="Open tracking in new tab"
                    />
                  ) : (
                    <Text>{packet.barcode}</Text>
                  )}
                  <CopyToClipboard
                    text={packet.barcode}
                    size="small"
                    type="text"
                  />
                </Space>
              ) : (
                EMPTY_VALUE
              ),
            },
          ]}
        />

        <Descriptions
          title="Carrier"
          bordered
          column={2}
          items={[
            {
              key: 'vendor',
              label: 'Vendor',
              children: (
                <Tag>{packet.CarrierService.CarrierVendorType.name}</Tag>
              ),
            },
            {
              key: 'service',
              label: 'Service',
              children: packet.CarrierService.name,
            },
            {
              key: 'service-id',
              label: 'Service ID',
              span: 2,
              children: packet.CarrierService.thirdPartyId,
            },
          ]}
        />

        <Descriptions
          title="Order"
          bordered
          column={1}
          items={[
            {
              key: 'order',
              label: 'Order',
              children: (
                <Link
                  to={`/order/${packet.Order.uuid}`}
                  label={packet.Order.uuid}
                  openInNewTab
                />
              ),
            },
          ]}
        />

        {packet.billedShippingPrice !== null && (
          <Descriptions
            title="Billing"
            bordered
            column={2}
            items={[
              {
                key: 'billed-original',
                label: 'Billed (original)',
                children: (
                  <>
                    {packet.billedShippingPrice?.toFixed(2)}{' '}
                    {packet.billedShippingCurrency}
                  </>
                ),
              },
              {
                key: 'billed-order-currency',
                label: 'Billed (order currency)',
                children:
                  packet.billedShippingPriceConverted !== null
                    ? `${packet.billedShippingPriceConverted.toFixed(2)} ${packet.Order.CurrencyType.symbol}`
                    : EMPTY_VALUE,
              },
              {
                key: 'billing-date',
                label: 'Billing date',
                children: packet.billedShippingDate
                  ? dayjs(packet.billedShippingDate).format(EDateFormat.DATE)
                  : EMPTY_VALUE,
              },
              {
                key: 'order-delivery-fee-excl-vat',
                label: 'Order delivery fee (excl. VAT)',
                children:
                  packet.Order.deliveryFeeWithoutTax !== null
                    ? `${packet.Order.deliveryFeeWithoutTax.toFixed(2)} ${packet.Order.CurrencyType.symbol}`
                    : EMPTY_VALUE,
              },
              ...(packet.billedShippingPriceConverted !== null &&
              packet.Order.deliveryFeeWithoutTax !== null
                ? [
                    {
                      key: 'difference',
                      label: 'Difference',
                      span: 2,
                      children: (() => {
                        const diff =
                          packet.billedShippingPriceConverted -
                          packet.Order.deliveryFeeWithoutTax;
                        const sign = diff >= 0 ? '+' : '';
                        return (
                          <Text type={diff > 0 ? 'danger' : 'success'}>
                            {sign}
                            {diff.toFixed(2)} {packet.Order.CurrencyType.symbol}
                          </Text>
                        );
                      })(),
                    },
                  ]
                : []),
            ]}
          />
        )}

        <Title level={5}>Status History</Title>
        <TableComponent
          columns={
            statusColumns as Parameters<typeof TableComponent>[0]['columns']
          }
          dataSource={statuses.map((s) => ({ ...s, key: s.id }))}
          isLoading={packetsInProcess}
          size="small"
          scroll={{ x: true }}
        />
      </Space>
    </>
  );
}

export default PacketContainer;
